import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchHealth } from "@/services/api";

export default function Home() {
  const navigate = useNavigate();
  const [health, setHealth] = useState<{ bilibili: boolean; source: string } | null>(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    fetchHealth().then(setHealth);
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const go = () => navigate("/watch");

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.code === "Space" || event.key === "Enter") {
        event.preventDefault();
        go();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const timeStr = now.toLocaleTimeString("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  return (
    <div className="acg-scene fixed isolate inset-0 bg-void text-phosphor overflow-hidden">
      <div className="absolute inset-0 crt-scanlines-static pointer-events-none opacity-30" />
      <div className="absolute top-[13%] left-[9%] text-3xl rotate-[-14deg]">✦</div>
      <div className="absolute top-[23%] right-[11%] text-4xl text-sakura rotate-[12deg]">♡</div>
      <div className="absolute bottom-[19%] right-[18%] text-2xl text-ocean rotate-[-18deg]">✧</div>

      <main className="relative z-10 h-full w-full flex flex-col items-center justify-between px-6 py-8 sm:py-10 safe-top safe-bottom">
        <header className="w-full max-w-5xl flex justify-between items-center text-xs text-static">
          <span className="acg-sticker px-3 py-1.5 font-bold tracking-wide">
            <span className="mr-1.5 text-signal">●</span> 未来已连接
          </span>
          <span className="acg-sticker px-3 py-1.5 font-mono tabular-nums">{timeStr}</span>
        </header>

        <motion.section
          className="relative flex flex-col items-center gap-7 max-w-3xl px-8 py-10 sm:px-16 sm:py-12 rounded-[30px] border-2 border-white/80 bg-white/50 shadow-[0_18px_60px_rgba(116,75,124,.18)]"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <div className="absolute -top-4 acg-sticker px-4 py-1.5 text-[10px] sm:text-xs font-bold tracking-[.22em] text-signal">
            BILIBILI TIME MACHINE
          </div>

          <div className="text-center space-y-4">
            <img src="/bilibili-color.svg" alt="Bilibili" className="w-12 h-12 mx-auto drop-shadow-[0_4px_8px_rgba(251,114,153,.3)]" />
            <h1 className="acg-title font-display font-black tracking-[.06em] text-[clamp(3.4rem,9vw,7rem)] leading-none">
              BILITIME
            </h1>
            <p className="font-bold text-sm tracking-[.3em] text-signal">哔哩时光机</p>
          </div>

          <p className="text-center text-static text-sm sm:text-base tracking-wide max-w-md leading-relaxed">
            从随机频道捞起一段旧回忆。<br />
            下一秒，会不会就是你收藏过的那个瞬间？
          </p>

          <motion.button
            onClick={go}
            className="acg-primary-button font-display font-bold tracking-[.24em] text-base sm:text-lg py-4 px-12 transition-all duration-200"
            whileTap={{ scale: 0.97 }}
            data-ui-zone
          >
            开始穿越 ✦
          </motion.button>

          <div className="text-[11px] font-medium tracking-wide text-static flex flex-col items-center gap-1.5">
            <span>按空格键，随机跳到一段旧时光</span>
            {health && (
              <span className="opacity-70">
                {health.bilibili ? "● B站信号正常" : "○ 暂时无法连接 B 站"}
              </span>
            )}
          </div>
        </motion.section>

        <footer className="w-full max-w-5xl flex justify-between items-end text-[10px] font-medium uppercase tracking-wide text-static">
          <span>v.0.1.0</span>
          <span>♡ archive · random · memory ♡</span>
        </footer>
      </main>
    </div>
  );
}
